import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import CloseIcon from '@material-ui/icons/Close';
import DeleteIcon from '@material-ui/icons/DeleteOutline';

/**
 * "View / Update Storage Locations" popup — BCM-234 (deck slides 14-17).
 *
 * Behaviour from the deck:
 *  • Rows list the storage locations currently attached to the product's plant.
 *  • Each editable row is a textbox + dropdown (Autocomplete with freeSolo): the
 *    user picks an existing storage location OR types a brand-new value. When the
 *    typed value matches nothing, the dropdown shows
 *    "No match — your typed value will be used".
 *  • A storage location already chosen in another row is not offered again.
 *  • readOnly (View Product) hides the Action column and both footer buttons.
 *  • Nothing is persisted here — "Save changes" hands the list back to the parent
 *    form, which submits it with the rest of the product on form submit.
 */
const useStyles = makeStyles((theme) => ({
  paper: {
    width: 680,
    maxWidth: '94vw',
    borderRadius: 10,
  },
  head: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '1.25rem 1.5rem 0.75rem',
  },
  title: {
    fontSize: '1.375rem',
    fontWeight: 700,
    color: '#1a1a1a',
  },
  tableWrap: {
    maxHeight: 340,
    overflowY: 'auto',
    borderTop: '1px solid #eceef1',
  },
  th: {
    fontSize: '0.9375rem',
    fontWeight: 700,
    color: '#1a1a1a',
    background: '#f7f8fa',
    borderBottom: '1px solid #eceef1',
    padding: '0.85rem 1.5rem',
    position: 'sticky',
    top: 0,
    zIndex: 2,
  },
  thAction: {
    textAlign: 'right',
  },
  td: {
    fontSize: '0.9375rem',
    color: '#1a1a1a',
    borderBottom: '1px solid #f1f2f4',
    padding: '0.85rem 1.5rem',
  },
  tdAction: {
    textAlign: 'right',
    borderBottom: '1px solid #f1f2f4',
    padding: '0.5rem 1.25rem',
    width: 90,
  },
  deleteIcon: {
    color: '#e03131',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '1rem',
    padding: '1.1rem 1.5rem 1.4rem',
  },
  addBtn: {
    textTransform: 'none',
    fontSize: '0.9375rem',
    fontWeight: 500,
    color: '#1a1a1a',
    border: '1px solid #c8ccd4',
    borderRadius: 24,
    padding: '8px 20px 8px 12px',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    background: '#fff',
    '&:hover': { background: '#f5f6f8' },
  },
  addCircle: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 22,
    height: 22,
    borderRadius: '50%',
    border: '1px solid #6b7280',
    fontSize: '0.9375rem',
    lineHeight: 1,
    marginRight: 8,
  },
  saveBtn: {
    background: '#ec6535',
    color: '#fff',
    textTransform: 'none',
    fontSize: '0.9375rem',
    fontWeight: 500,
    borderRadius: 24,
    padding: '9px 26px',
    boxShadow: 'none',
    '&:hover': { background: '#d4562a', boxShadow: 'none' },
  },
  emptyHint: {
    padding: '1.5rem',
    textAlign: 'center',
    color: '#6b7280',
    fontSize: '0.875rem',
  },
  noMatch: {
    fontSize: '0.875rem',
    color: '#8a8f98',
    padding: '0.5rem 0.25rem',
  },
}));

// freeSolo Autocomplete suppresses MUI's own "no options" panel (any typed value
// is legal), but the deck still wants an explicit hint. Inject a disabled
// sentinel option instead so the message appears in the dropdown as designed.
export const NO_MATCH_OPTION = '__no_match__';
export const NO_MATCH_LABEL = 'No match — your typed value will be used';

export const freeSoloFilter = (opts, state) => {
  const input = String(state.inputValue || '').trim().toLowerCase();
  const filtered = (opts || []).filter((o) =>
    String(o).toLowerCase().includes(input)
  );
  if (input && filtered.length === 0) return [NO_MATCH_OPTION];
  return filtered;
};

const StorageLocationsDialog = ({
  open,
  onClose,
  // Current list for this product, e.g. ['INTR','0005'].
  value = [],
  // Every storage location already known to the backend (dropdown source).
  options = [],
  onSave,
  readOnly = false,
}) => {
  const classes = useStyles();
  // Local draft so Cancel/close discards edits; committed only via Save changes.
  // Each row is { value, isNew }: rows that already existed render as plain text
  // (deck slide 15) and only rows added in this session get the editable
  // textbox+dropdown (deck slides 16-17).
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (open) {
      setRows(
        (Array.isArray(value) ? value : []).map((v) => ({ value: v, isNew: false }))
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // A value already used by another row must not be offered again (deck slide 16).
  const availableFor = useMemo(() => {
    const taken = rows.map((r) => String(r.value || '').trim()).filter(Boolean);
    return (index) => {
      const mine = String(rows[index]?.value || '').trim();
      return (options || []).filter((opt) => opt === mine || !taken.includes(opt));
    };
  }, [rows, options]);

  const setRowValue = (index, next) => {
    setRows((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], value: next == null ? '' : String(next) };
      return copy;
    });
  };

  const removeRow = (index) =>
    setRows((prev) => prev.filter((_, i) => i !== index));

  const addRow = () => setRows((prev) => [...prev, { value: '', isNew: true }]);

  const handleSave = () => {
    // Drop blank/duplicate rows so the parent never receives junk.
    const cleaned = [];
    rows.forEach((r) => {
      const v = String(r.value || '').trim();
      if (v && !cleaned.includes(v)) cleaned.push(v);
    });
    if (onSave) onSave(cleaned);
    onClose();
  };

  return (
    <Dialog open={!!open} onClose={onClose} maxWidth={false} classes={{ paper: classes.paper }}>
      <div className={classes.head}>
        <Typography className={classes.title}>View / Update Storage Locations</Typography>
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </div>

      <div className={classes.tableWrap}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell className={classes.th}>Storage Location</TableCell>
              {!readOnly && (
                <TableCell className={`${classes.th} ${classes.thAction}`}>Action</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={readOnly ? 1 : 2} className={classes.emptyHint}>
                  {readOnly
                    ? 'No storage locations added.'
                    : 'No storage locations yet — use “Add New Storage Location” below.'}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, index) => (
                <TableRow key={`sloc-row-${index}`}>
                  <TableCell className={classes.td}>
                    {readOnly || !row.isNew ? (
                      // Existing storage locations are shown as plain text —
                      // they can be deleted but not renamed (deck slide 15).
                      row.value
                    ) : (
                      <Autocomplete
                        freeSolo
                        openOnFocus
                        disableClearable
                        options={availableFor(index)}
                        inputValue={row.value || ''}
                        onInputChange={(e, newInput, reason) => {
                          // Same guard as the Plant ID field: MUI fires
                          // reason 'reset' with '' on mount and when `options`
                          // changes (the storage-location list loads
                          // asynchronously), which would wipe a typed value.
                          if (reason === "reset" && !newInput) return;
                          setRowValue(index, newInput);
                        }}
                        onChange={(e, picked) => {
                          if (picked === NO_MATCH_OPTION) return;
                          setRowValue(index, picked);
                        }}
                        filterOptions={freeSoloFilter}
                        getOptionDisabled={(opt) => opt === NO_MATCH_OPTION}
                        renderOption={(opt) =>
                          opt === NO_MATCH_OPTION ? (
                            <span className={classes.noMatch}>{NO_MATCH_LABEL}</span>
                          ) : (
                            opt
                          )
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            size="small"
                            placeholder="Type or select"
                          />
                        )}
                      />
                    )}
                  </TableCell>
                  {!readOnly && (
                    <TableCell className={classes.tdAction}>
                      <IconButton
                        size="small"
                        aria-label="Delete storage location"
                        onClick={() => removeRow(index)}
                      >
                        <DeleteIcon fontSize="small" className={classes.deleteIcon} />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {!readOnly && (
        <div className={classes.footer}>
          <Button className={classes.addBtn} onClick={addRow} disableRipple>
            <span className={classes.addCircle}>+</span>
            Add New Storage Location
          </Button>
          <Button variant="contained" className={classes.saveBtn} onClick={handleSave} disableElevation>
            Save changes
          </Button>
        </div>
      )}
    </Dialog>
  );
};

export default StorageLocationsDialog;
